// Rules come from snippets/cart-free-gift.liquid as a JSON plan; /cart.js has no metafields.
(function () {
  'use strict';

  // Only lines carrying MARKER are ever removed, so customer-added lines are never touched.
  const MARKER = '_gp_free_gift';
  const TRIGGER_PROP = '_gp_free_gift_trigger';
  const RULE_PROP = '_gp_free_gift_rule';

  // A cart attribute so dismissal survives refresh and cart restore.
  const DISMISS_ATTR = '_gp_gift_dismissed';

  const MUTATION_BUDGET = 4;

  // Hard stop: gifts are added post-checkout by a Shopify Flow, so never add gift lines to the cart.
  const CART_WRITES_DISABLED = true;

  const isOurGift = (item) => !!(item && item.properties && item.properties[MARKER]);

  const variantOf = (item) => Number(item.variant_id || item.id);

  // Gift lines are always excluded so a priced gift can't qualify itself; money is integer pence.
  function qualifyingSubtotal(cart, basis) {
    const items = (cart && cart.items) || [];
    const giftTotal = items.filter(isOurGift).reduce((sum, i) => sum + (i.final_line_price || 0), 0);

    if (basis === 'cart_total') {
      return Math.max(0, ((cart && cart.total_price) || 0) - giftTotal);
    }

    const field = basis === 'before_discounts' ? 'original_line_price' : 'final_line_price';

    return items.filter((i) => !isOurGift(i)).reduce((sum, i) => sum + (i[field] || 0), 0);
  }

  // Mirrors snippets/cart-free-gift.liquid.
  function hasSubscription(cart) {
    return ((cart && cart.items) || []).some((i) => !isOurGift(i) && !!i.selling_plan_allocation);
  }

  // Must match the case statement in snippets/cart-free-gift.liquid; returns null when qualified.
  function qualifyReason(cart, plan, subtotal) {
    const trigger = (plan && plan.trigger) || 'spend';
    const threshold = Number(plan && plan.threshold) || 0;

    if (trigger === 'subscription') {
      return hasSubscription(cart) ? null : 'no-subscription';
    }

    // A zero threshold means not configured, not that everything qualifies.
    if (threshold <= 0) return 'no-threshold';
    if (subtotal < threshold) return 'below-threshold';
    if (trigger === 'both' && !hasSubscription(cart)) return 'no-subscription';

    return null;
  }

  function dismissedVariants(cart) {
    const raw = ((cart && cart.attributes) || {})[DISMISS_ATTR] || '';

    return String(raw)
      .split(',')
      .map((value) => Number(String(value).trim()))
      .filter((value) => value > 0);
  }

  function decide(cart, plan) {
    const settings = plan || {};
    const items = (cart && cart.items) || [];
    const basis = settings.basis || 'after_discounts';
    const threshold = Number(settings.threshold) || 0;

    const subtotal = qualifyingSubtotal(cart, basis);
    const remaining = Math.max(0, threshold - subtotal);
    const empty = { desired: [], subtotal, threshold, remaining, qualified: false };

    if (!settings.enabled) return Object.assign({}, empty, { reason: 'disabled' });
    if (!items.length) return Object.assign({}, empty, { reason: 'empty-cart' });

    const notQualified = qualifyReason(cart, settings, subtotal);
    if (notQualified) return Object.assign({}, empty, { reason: notQualified });

    const dismissed = settings.dismissal === 'remember' ? dismissedVariants(cart) : [];
    const fallback = settings.fallback;

    const resolved = [];

    (settings.candidates || []).forEach((candidate, index) => {
      const priority = Number(candidate.priority);
      const base = {
        trigger: Number(candidate.trigger) || null,
        priority: Number.isFinite(priority) ? priority : 9999,
        order: index,
      };

      if (candidate.available && !(settings.unavailable || []).includes(Number(candidate.gift))) {
        resolved.push(Object.assign({}, base, {
          variant: Number(candidate.gift),
          title: candidate.giftTitle || '',
          rule: 'threshold',
        }));
        return;
      }

      if (
        settings.oosFallback === 'default_gift' &&
        fallback &&
        fallback.available &&
        !(settings.unavailable || []).includes(Number(fallback.gift))
      ) {
        resolved.push(Object.assign({}, base, {
          variant: Number(fallback.gift),
          title: fallback.giftTitle || '',
          rule: 'fallback',
        }));
        return;
      }

      resolved.push(Object.assign({}, base, { variant: null, title: candidate.giftTitle || '', rule: 'unavailable' }));
    });

    const givable = resolved.filter((entry) => entry.variant);
    const blocked = resolved.filter((entry) => !entry.variant);

    // Lower priority number wins; ties break on cart line order.
    const ordered = givable.slice().sort((a, b) => a.priority - b.priority || a.order - b.order);

    const seen = new Set();
    const unique = ordered.filter((entry) => {
      if (seen.has(entry.variant)) return false;
      seen.add(entry.variant);
      return true;
    });

    const allowed = unique.filter((entry) => !dismissed.includes(entry.variant));

    const chosen = settings.multi === 'all' ? allowed : allowed.slice(0, 1);

    return {
      desired: chosen.map((entry) => ({
        variant: entry.variant,
        quantity: 1,
        trigger: entry.trigger,
        rule: entry.rule,
        title: entry.title,
      })),
      subtotal,
      threshold,
      remaining: 0,
      qualified: true,
      blocked,
      dismissed,
      reason: chosen.length ? 'gift' : allowed.length === 0 && unique.length ? 'dismissed' : blocked.length ? 'unavailable' : 'no-candidates',
    };
  }

  function newlyRemovedGifts(cart, plan, removedByUs) {
    if (!plan || plan.dismissal !== 'remember') return [];

    const before = (plan.giftVariants || []).map(Number).filter(Boolean);
    if (!before.length) return [];

    const now = new Set(((cart && cart.items) || []).filter(isOurGift).map(variantOf));
    const ours = new Set((removedByUs || []).map(Number));

    const gone = before.filter((variant) => !now.has(variant) && !ours.has(variant));
    if (!gone.length) return [];

    if (qualifyReason(cart, plan, qualifyingSubtotal(cart, plan.basis))) return [];

    const already = dismissedVariants(cart);
    const merged = Array.from(new Set(already.concat(gone)));

    return merged.length === already.length ? [] : merged;
  }

  function planMutations(cart, desired) {
    const giftLines = ((cart && cart.items) || []).filter(isOurGift);
    const wanted = new Map(desired.map((entry) => [entry.variant, entry]));

    const remove = [];
    const keep = new Set();

    giftLines.forEach((line) => {
      const variant = variantOf(line);
      const want = wanted.get(variant);

      // Gift lines have no stepper, so any quantity other than the desired one came from outside.
      if (!want || keep.has(variant) || line.quantity !== want.quantity) {
        remove.push({ key: line.key, variant, quantity: line.quantity });
        return;
      }

      keep.add(variant);
    });

    const add = desired.filter((entry) => !keep.has(entry.variant));

    return { add, remove };
  }

  class GPFreeGift extends HTMLElement {
    connectedCallback() {
      this._onCartUpdate = this._onCartUpdate.bind(this);

      GPFreeGift.applying = GPFreeGift.applying || false;

      if (window.cartState) {
        window.cartState.addEventListener('cart:updated', this._onCartUpdate);
      }

      this._run(window.cartState && window.cartState.cart);
    }

    disconnectedCallback() {
      if (window.cartState) window.cartState.removeEventListener('cart:updated', this._onCartUpdate);
    }

    get plan() {
      if (this._plan) return this._plan;

      const holder = this.querySelector('[data-gp-gift-plan]');
      if (!holder) return null;

      try {
        this._plan = JSON.parse(holder.textContent);
      } catch (error) {
        console.warn('[gp-gift] plan did not parse', error);
        this._plan = null;
      }

      return this._plan;
    }

    // Fires before the re-render, so the plan may be stale; _run's fingerprint check guards that.
    _onCartUpdate(event) {
      this._run((event && event.detail && event.detail.cart) || (window.cartState && window.cartState.cart));
    }

    // Loop guards: fingerprint match, idempotent planMutations, and a static MUTATION_BUDGET.
    _run(cart) {
      if (CART_WRITES_DISABLED) return;

      const plan = this.plan;
      if (!plan || !plan.enabled) return;
      if (GPFreeGift.applying) return;

      if (GPFreeGift.spent >= MUTATION_BUDGET) return;

      if (cart) {
        // Must run before the fingerprint check: a deletion is only visible while the plan is stale.
        this._resetDismissalOnEmptyBasket(cart);
        this._detectDismissal(cart, plan);

        if (plan.fingerprint !== fingerprint(cart)) return;
      } else {
        if (plan.wantsAction && window.cartState) window.cartState.refresh();
        return;
      }

      const outcome = decide(cart, Object.assign({}, plan, { unavailable: GPFreeGift.unavailable }));
      const mutations = planMutations(cart, outcome.desired);

      if (!mutations.add.length && !mutations.remove.length) {
        GPFreeGift.spent = 0;
        GPFreeGift.lastRemoved = [];
        return;
      }

      GPFreeGift.spent = (GPFreeGift.spent || 0) + 1;
      this._apply(mutations, outcome);
    }

    // /cart/clear.js keeps attributes, so clear the dismissal on an empty basket; '' deletes it.
    _resetDismissalOnEmptyBasket(cart) {
      if (!cart || cart.item_count) return;
      if (!dismissedVariants(cart).length) return;
      if (!window.cartState || typeof window.cartState.update !== 'function') return;

      GPFreeGift.applying = true;
      window.cartState
        .update({ attributes: { [DISMISS_ATTR]: '' } })
        .catch((error) => console.warn('[gp-gift] could not clear the dismissal', error))
        .finally(() => {
          GPFreeGift.applying = false;
        });
    }

    _detectDismissal(cart, plan) {
      const merged = newlyRemovedGifts(cart, plan, GPFreeGift.lastRemoved);
      if (!merged.length) return;

      if (!window.cartState || typeof window.cartState.update !== 'function') return;

      GPFreeGift.applying = true;
      window.cartState
        .update({ attributes: { [DISMISS_ATTR]: merged.join(',') } })
        .catch((error) => console.warn('[gp-gift] could not record the dismissal', error))
        .finally(() => {
          GPFreeGift.applying = false;
        });
    }

    // Removals before additions so a swapped gift is never held twice.
    _apply(mutations, outcome) {
      if (!window.cartState) return;

      GPFreeGift.applying = true;
      GPFreeGift.lastRemoved = mutations.remove.map((entry) => entry.variant);

      const removals = mutations.remove.reduce(
        (chain, entry) => chain.then(() => window.cartState.change({ id: entry.key, quantity: 0 })),
        Promise.resolve()
      );

      removals
        .then(() => this._addAll(mutations.add, outcome))
        .catch((error) => console.warn('[gp-gift] gift mutation failed', error))
        .finally(() => {
          GPFreeGift.applying = false;

          // Re-run on the live element: a pass during `applying` would have bailed out.
          const live = (typeof document !== 'undefined' && document.querySelector('gp-free-gift')) || this;
          live._run(window.cartState && window.cartState.cart);
        });
    }

    // A failed add marks the variant unavailable so the out-of-stock fallback applies.
    _addAll(entries, outcome) {
      if (!entries.length) return Promise.resolve();

      return entries.reduce(
        (chain, entry) =>
          chain.then(() =>
            window.cartState
              .add([
                {
                  id: entry.variant,
                  quantity: entry.quantity,
                  properties: {
                    [MARKER]: 'gp-cart-drawer',
                    [TRIGGER_PROP]: String(entry.trigger || ''),
                    [RULE_PROP]: `${entry.rule}:${outcome.threshold}`,
                  },
                },
              ])
              .catch((error) => {
                console.warn(`[gp-gift] variant ${entry.variant} could not be added`, error);
                GPFreeGift.unavailable = (GPFreeGift.unavailable || []).concat(entry.variant);
              })
          ),
        Promise.resolve()
      );
    }
  }

  // Must match the fingerprint in snippets/cart-free-gift.liquid exactly or the engine never acts.
  function fingerprint(cart) {
    const items = (cart && cart.items) || [];
    return `${(cart && cart.item_count) || 0}:${items.map((item) => item.key).join(',')}`;
  }

  GPFreeGift.applying = false;
  GPFreeGift.spent = 0;
  GPFreeGift.lastRemoved = [];
  GPFreeGift.unavailable = [];

  // Exported for tools/cart-harness.mjs.
  window.gpFreeGift = {
    decide,
    planMutations,
    newlyRemovedGifts,
    qualifyingSubtotal,
    hasSubscription,
    qualifyReason,
    dismissedVariants,
    fingerprint,
    isOurGift,
    MARKER,
    TRIGGER_PROP,
    RULE_PROP,
    DISMISS_ATTR,
    MUTATION_BUDGET,
  };

  if (!customElements.get('gp-free-gift')) {
    customElements.define('gp-free-gift', GPFreeGift);
  }
})();
